/**
 * "Hire employee" — provision an AI employee principal.
 *
 * One POST creates the principal, grants the role and mints the first token.
 * The 201 carries the plaintext token, which the server cannot show again, so
 * the modal switches to a copy-once view instead of closing on success.
 */
import { useState } from "react";

import { adminApi } from "../../api/admin";
import { Modal } from "../../components/Modal";
import { SecretOnce } from "../../components/admin/SecretOnce";
import { useToast } from "../../components/toast/context";

const ROLES = [
  { value: "agent-worker", label: "agent-worker — executes queued jobs" },
  { value: "agent-cos", label: "agent-cos — chief of staff, fields questions" },
  {
    value: "agent-mailroom",
    label: "agent-mailroom — inbox steward, reads all cleared mail, drafts only",
  },
  {
    value: "agent-growth",
    label: "agent-growth — SEA + campaigns + pipelines; CRM writes, known-contact mail only",
  },
  {
    value: "agent-travel",
    label: "agent-travel — Duffel travel search + gated booking",
  },
  {
    value: "agent-books",
    label: "agent-books — bookkeeping; reads all cleared mail + attachments, gated drafts/sends, no CRM writes",
  },
  {
    value: "agent-access",
    label: "agent-access — B2G market access; country list, CRM writes for public payers, review-first email",
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the new employee's id once the token has been acknowledged. */
  onCreated: (agentId: string) => void;
}

export function AgentCreateModal({ open, onClose, onCreated }: Props) {
  const createMutation = adminApi.aiTeam.createAgent();
  const toast = useToast();

  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [roleName, setRoleName] = useState(ROLES[0].value);
  const [minted, setMinted] = useState<{ id: string; token: string } | null>(
    null,
  );

  const reset = () => {
    setSlug("");
    setName("");
    setRoleName(ROLES[0].value);
    setMinted(null);
  };

  const handleClose = () => {
    const createdId = minted?.id;
    reset();
    onClose();
    if (createdId) onCreated(createdId);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedSlug = slug.trim().toLowerCase();
    const trimmedName = name.trim();
    if (!trimmedSlug || !trimmedName) {
      toast.error("Slug and name are required");
      return;
    }
    createMutation.mutate(
      { agent_slug: trimmedSlug, name: trimmedName, role_name: roleName },
      {
        onSuccess: (agent) => setMinted({ id: agent.id, token: agent.token }),
        onError: (e) => toast.error(e),
      },
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={minted ? "Employee hired" : "Hire employee"}
        eyebrow="Team"
      >
        {minted ? (
          <div className="space-y-4">
            <p className="text-sm text-ink-medium">
              The principal, its role grant and its first token exist. Put the
              token in the runner's environment.
            </p>
            <SecretOnce secret={minted.token} label="Agent token" />
            <div className="flex justify-end pt-2 border-t border-rule">
              <button type="button" className="btn btn-main" onClick={handleClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label" htmlFor="agent-slug">
                Slug
              </label>
              <input
                id="agent-slug"
                className="input"
                placeholder="cos"
                required
                autoFocus
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
              />
              <p className="text-xs text-ink-low mt-1">
                Lowercase identity of the employee — it names its queue scope
                and shows up as the asker in the question log.
              </p>
            </div>
            <div>
              <label className="label" htmlFor="agent-name">
                Name
              </label>
              <input
                id="agent-name"
                className="input"
                placeholder="Chief of Staff"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="agent-role">
                Role
              </label>
              <select
                id="agent-role"
                className="input"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-rule">
              <button type="button" className="btn btn-ghost" onClick={handleClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-main"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? "Hiring…" : "Hire"}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
